import { message } from 'antd';
import i18n from 'i18next';
import isEqual from 'lodash.isequal';
import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import {
  EditFormStoreEvent,
  EditFormStoreEventKind,
  EditFormStoreRowClickEvent,
  isEditFormStoreRowClickEvent,
  isEditFormStoreSetupButtonClickEvent,
} from 'config/pbEvents';
import { normalizeDictionarySettingValue } from 'entities/DictionarySettings';
import { FieldType } from 'entities/common';
import { DictionarySettingsEntityApi } from 'entities/endpoints/enum.get';
import { DictionaryUpdateApiParams } from 'entities/endpoints/enum.update';
import { ApiRequest, ToggleStateStore, Unsubscribe } from 'stores/common';
import { ILocalStore, IServerEntity } from 'stores/interfaces';
import { EditFormStoreMediator } from 'stores/local/forms/EditFormStore/EditFormStoreMediator';
import { TableRowEntityType } from 'stores/local/forms/types';
import { arrayMoveImmutable } from 'utils/arrayMoveImmutable';
import { generateHash } from 'utils/generateHash';
import { printErrors } from 'utils/printErrors';

import { FieldSettingsStoreCommonParams } from '../types';

import { DictionarySettingsDefaultValue } from './DictionarySettingsDefaultValue';
import { DictionarySettingsValueModel } from './DictionarySettingsValueModel';
import { IDictionarySettingsStore } from './interface';

type PrivateFields =
  | '_values'
  | '_setApiData'
  | '_isDataChanged'
  | '_setValues'
  | '_currentEntity'
  | '_isChangedDefaultValue';

type DictionarySettingsStoreParams = {
  handleSaveForm: () => Promise<void>;
} & FieldSettingsStoreCommonParams;

/**
 * Стор для работы с дровером справочник
 * todo: задекомпозировать стор, чтобы облегчить чтение кода
 */
export class DictionarySettingsStore
  implements
    ILocalStore,
    IServerEntity<DictionaryUpdateApiParams>,
    IDictionarySettingsStore
{
  constructor({
    mediator,
    getTableRowEntityByCatalogId,
    handleSaveForm,
  }: DictionarySettingsStoreParams) {
    this._mediator = mediator;
    this._handleSaveForm = handleSaveForm;

    this._subscribeToSetupButtonClickEvent =
      this._subscribeToSetupButtonClickEvent.bind(this);
    this._handleEventRowClickEvent = this._handleEventRowClickEvent.bind(this);

    this.dictionarySettingsDefaultValue = new DictionarySettingsDefaultValue({
      store: this,
      setHighlightedRows: (keys) =>
        this._mediator.emitSetHighlightedRowsEvent({ rows: keys }),
      getTableRowEntityByCatalogId,
    });

    this._storeInitSubscriber = this._mediator.subscribeToSetupButtonClickEvent(
      this._subscribeToSetupButtonClickEvent
    );

    this._controllerReaction = reaction(
      () => this.drawerController.opened,
      (opened) => {
        if (!opened) {
          this._resetRowSelectSubscription();
          this._mediator.emitToggleSettingsDrawerEvent({ payload: opened });
          this._reset();
        }
      }
    );

    makeObservable<DictionarySettingsStore, PrivateFields>(this, {
      _values: observable.ref,
      values: computed,
      _setValues: action.bound,
      createNewValue: action.bound,
      deleteItem: action.bound,
      allValuesUnique: computed,
      allValuesNotEmpty: computed,

      _isDataChanged: computed,
      _isChangedDefaultValue: computed,
      buttonDisabled: computed,

      toServerFormat: computed,
      _setApiData: action.bound,
      updateDictionary: action.bound,
      fetchDictionaryById: action.bound,

      _currentEntity: observable.ref,

      id: computed,
      dictionaryName: computed,

      handleSave: action.bound,

      onSortEnd: action.bound,
      resetToInitial: action.bound,
    });
  }

  readonly drawerController = new ToggleStateStore();

  private readonly _mediator: EditFormStoreMediator;

  private readonly _handleSaveForm: () => Promise<void>;

  private _reaction: IReactionDisposer | null = null;

  private _rowSelectSubscriber: Unsubscribe | null = null;
  private readonly _storeInitSubscriber: Unsubscribe;
  private readonly _controllerReaction: IReactionDisposer;

  /** Текущая выбранная сущность из таблицы */
  private _currentEntity: TableRowEntityType | null = null;

  /**
   * Список полей
   * @private
   */
  private _values: DictionarySettingsValueModel[] = [];

  readonly dictionaryGetRequest = new ApiRequest({
    endpoint: ENDPOINTS.enum.get,
    methodType: 'GET',
  });

  readonly dictionarySettingsDefaultValue: DictionarySettingsDefaultValue;

  /**
   * Предыдущие значения (нужна для сравнения на изменение)
   * @private
   */
  private _prevData: DictionaryUpdateApiParams = {
    field_id: this.id,
    items: [],
  };

  readonly dictionaryUpdateRequest = new ApiRequest({
    endpoint: ENDPOINTS.enum.update,
    methodType: 'POST',
  });

  get toServerFormat(): DictionaryUpdateApiParams {
    return {
      field_id: this.id,
      items: this.values.map((elem) => elem.toServerFormat),
    };
  }

  /**
   * флаг, который показывает: изменился ли справочник
   */
  private get _isDataChanged(): boolean {
    return !isEqual(this._prevData, this.toServerFormat);
  }

  private get _isChangedDefaultValue(): boolean {
    return (
      this._currentEntity?.formField.defaultValue?.value !==
      this.dictionarySettingsDefaultValue.toServerFormat?.value
    );
  }

  get values(): DictionarySettingsValueModel[] {
    return this._values;
  }

  get id(): number {
    return this._currentEntity?.catalogField.id || 0;
  }

  get dictionaryName(): string {
    return this._currentEntity?.catalogField.label || '';
  }

  /**
   * Блочим кнопку сохранения в следующих случаях:
   * 1. Не поменялось значение по умолчанию или не добавили новое значение
   * 2. Добавили значение, но оно совпадает с другими значениями справочника
   * 3. Есть пустые значения среди значений справочника
   * */
  get buttonDisabled(): boolean {
    return !(
      (this._isChangedDefaultValue || this._isDataChanged) &&
      this.allValuesUnique &&
      this.allValuesNotEmpty
    );
  }

  /** Проверяем, есть ли повторяющиеся значения среди опций */
  get allValuesUnique(): boolean {
    const names = this._values.map((item) => item.value);

    return names.length === new Set(names).size;
  }

  get allValuesNotEmpty(): boolean {
    return !this._values.some((item) => item.value.trim() === '');
  }

  /** Считаем, что значение выбрано, если:
   * 1. Все значения уникальны ( избегаем коллизий среди одинаковых значений, иначе будет выбрано несколько значений сразу )
   * 2. Значение не пустое
   * */
  isValueChecked(entity: DictionarySettingsValueModel): boolean {
    return (
      this.dictionarySettingsDefaultValue.defaultValue.trim() !== '' &&
      this.allValuesUnique &&
      this.dictionarySettingsDefaultValue.defaultValue.trim() === entity.value
    );
  }

  private _setCurrentEntity(entity: TableRowEntityType): void {
    this._currentEntity = entity;
  }

  private _handleEventRowClickEvent(event: EditFormStoreRowClickEvent): void {
    this.dictionarySettingsDefaultValue.handleSelectRow(event.payload);
  }

  private _subscribeToRowSelectEvent(event: EditFormStoreEvent): void {
    if (isEditFormStoreRowClickEvent(event)) {
      this._rowSelectSubscriber = this._mediator.subscribeToRowClickEvent(
        this._handleEventRowClickEvent
      );

      this._reaction = reaction(
        () =>
          this.dictionarySettingsDefaultValue.newFormulaModel.usedFormFieldIds,
        (keys) => {
          this._mediator.emitSetHighlightedRowsEvent({ rows: keys });
        }
      );
    }
  }

  private _subscribeToSetupButtonClickEvent(event: EditFormStoreEvent): void {
    if (isEditFormStoreSetupButtonClickEvent(event)) {
      if (event.payload.catalogField.dataType !== FieldType.enum) {
        return;
      }

      this.drawerController.open();
      this._setCurrentEntity(event.payload);
      this._subscribeToRowSelectEvent({
        kind: EditFormStoreEventKind.ROW_CLICK,
        payload: event.payload,
      });
    }
  }

  private _resetHighLightedRows(): void {
    this._mediator.emitSetHighlightedRowsEvent({ rows: [] });
  }

  private _resetRowSelectSubscription(): void {
    this._rowSelectSubscriber?.();
    this._reaction?.();
    this._reaction = null;
    this._resetHighLightedRows();
  }

  private _setValues(newValues: DictionarySettingsValueModel[]): void {
    this._values = newValues;
  }

  /**
   * Поиск индекса элемента по id элемента
   * @param id
   */
  findIndexById(id: number): number {
    return this.values.findIndex((el) => el.id === id);
  }

  /**
   * Сортировка полей, юзается на драг-н-дроп а так же на выбор порядка с помощью селектора
   * @param oldIndex
   * @param newIndex
   */
  onSortEnd({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        this._values.slice(),
        oldIndex,
        newIndex
      ).filter((el: DictionarySettingsValueModel) => Boolean(el));

      this._setValues(newData);
    }
  }

  /**
   * Удаление элемента
   * @param id
   */
  deleteItem(id: number | string): void {
    this._setValues(this._values.filter((el) => el.id !== id));
  }

  async handleSave(): Promise<void> {
    /** Обновляем поля для самого справочника */
    if (this._isDataChanged) {
      await this.updateDictionary();
    }

    if (this.dictionaryUpdateRequest.error) {
      message.error(i18n.t('dictionary.alertError'));
    }

    /** Обновляем значение по умолчанию для текущей редактируемой строки таблицы */
    if (this._isChangedDefaultValue) {
      this._currentEntity?.formField.setDefaultValue(
        this.dictionarySettingsDefaultValue.toServerFormat
      );
    }

    await this._handleSaveForm();

    message.success(i18n.t('dictionary.alertSuccess'));
    this.drawerController.close();
  }

  /**
   * Запрос за настройками справочника
   */
  async fetchDictionaryById(): Promise<void> {
    try {
      const { data, isError } =
        await this.dictionaryGetRequest.call<DictionarySettingsEntityApi>({
          data: { field_id: this.id },
        });

      if (data && !isError) {
        this._prevData = { field_id: this.id, items: data.items };
        this._setApiData(data);
      }
    } catch (e) {
      printErrors(e);
    }
  }

  /**
   * метод отмены обновлений
   */
  resetToInitial() {
    this._setApiData(this._prevData);
  }

  /**
   * Нормализация и сеттинг данных в values
   * @param data
   */
  private _setApiData(data: DictionarySettingsEntityApi) {
    this._setValues(
      data.items.map(
        (elem) =>
          new DictionarySettingsValueModel(
            normalizeDictionarySettingValue(elem)
          )
      )
    );

    if (!this._currentEntity) {
      return;
    }

    this.dictionarySettingsDefaultValue.init(this._currentEntity);
  }

  /**
   * Метод обновления
   */
  async updateDictionary(): Promise<void> {
    const requestData = this.toServerFormat;

    try {
      const { data } =
        await this.dictionaryUpdateRequest.call<DictionarySettingsEntityApi>({
          data: requestData,
        });

      if (data) {
        this._prevData = {
          field_id: this.id,
          items: data.items,
        };
        this._setValues(
          data.items.map(
            (elem) =>
              new DictionarySettingsValueModel(
                normalizeDictionarySettingValue(elem)
              )
          )
        );
      }
    } catch (e) {
      printErrors(e);
    }
  }

  /**
   * создание новго поля
   */
  createNewValue(): void {
    this._setValues([
      ...this._values,
      new DictionarySettingsValueModel({
        id: generateHash(),
        value: '',
        fieldId: this.id,
      }),
    ]);
  }

  private _reset(): void {
    this._setValues([]);
    this.dictionarySettingsDefaultValue.reset();
  }

  destroy() {
    this.dictionaryUpdateRequest.destroy();
    this.dictionaryGetRequest.destroy();
    this._storeInitSubscriber?.();
    this.dictionarySettingsDefaultValue.destroy();
    this.values.forEach((elem) => elem.destroy());
  }
}
